import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { availableCredits } from '@/lib/availableCredits';
import { formatCreditBalance } from '@/lib/formatCredits';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

export const CREDIT_HOLD_EXPLANATION = 'The final cost is only known when the task finishes. We reserve extra credits in case it costs more than estimated. Any unused credits become available again.';

export const creditCardStyles = dark => StyleSheet.create({
  card: { width: '100%', maxWidth: 600, padding: 24, gap: 20, borderWidth: 1, borderRadius: 16, backgroundColor: dark ? '#1C1C1C' : '#FFFFFF', borderColor: dark ? '#383838' : '#E5E5E5' },
  heading: { gap: 8 },
  title: { color: dark ? '#FFFFFF' : '#040404', lineHeight: 28, letterSpacing: -0.4 },
  primary: { color: dark ? '#FFFFFF' : '#040404', fontVariant: ['tabular-nums'] },
  secondary: { color: dark ? '#B3B3B3' : '#616161', lineHeight: 22 },
  ledger: { gap: 12 },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 },
  label: { flexShrink: 1 },
  estimate: { borderTopWidth: 1, borderColor: dark ? '#383838' : '#E5E5E5', paddingTop: 16, marginTop: 4 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
  button: { minHeight: 44, borderRadius: 10, paddingHorizontal: 18 },
  secondaryButton: { backgroundColor: 'transparent', borderColor: '#808080' },
  disclosure: { minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start' },
});

export default function WorkCreditSummary({ credits, requiredCredits, title, description, blockedByHold = false }) {
  const [expanded, setExpanded] = useState(false);
  const { theme } = useTheme();
  const styles = creditCardStyles(isDarkTheme(theme));
  const available = availableCredits(credits);
  const reserved = credits?.reservedCredits;
  const total = credits?.totalAvailable;
  const number = value => Number.isFinite(value) ? formatCreditBalance(value) : '—';
  const row = (label, value, emphasized = false) => <View style={styles.row}>
    <Text size="s" weight={emphasized ? 'semibold' : 'regular'} style={[emphasized ? styles.primary : styles.secondary, styles.label]}>{label}</Text>
    <Text size="s" weight={emphasized ? 'semibold' : 'regular'} style={styles.primary}>{number(value)}</Text>
  </View>;
  return <>
    <View style={styles.heading}>
      <Text size="xl" weight="semibold" accessibilityRole="header" style={styles.title}>{title}</Text>
      {blockedByHold ? <>
        <Text size="s" style={styles.secondary}>{requiredCredits > 0
          ? `This task needs a temporary reserve in addition to its estimated cost of ${number(Math.ceil(requiredCredits))} credits.`
          : 'This task needs a temporary credit reserve because its final cost can vary.'}</Text>
        <Text size="s" style={styles.primary}>{Number.isFinite(reserved) && Number.isFinite(total) && Number.isFinite(available)
          ? available === 0 && total > 0
            ? `Your ${number(total)} credits are fully reserved, leaving none available to continue.`
            : `After reserving ${number(reserved)} credits, only ${number(available)} are available to continue.`
          : 'After the reserve, you don’t have enough available credits to continue.'}</Text>
      </> : !!description && <Text size="s" style={styles.secondary}>{description}</Text>}
    </View>
    {blockedByHold ? <View>
      <Pressable accessibilityRole="button" accessibilityLabel="Why are credits reserved?" accessibilityState={{ expanded }}
        onPress={() => setExpanded(value => !value)} style={styles.disclosure}>
        <Text size="s" style={styles.secondary}>Why are credits reserved?</Text>
        <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={10} color={styles.secondary.color} />
      </Pressable>
      {expanded && <Text size="s" style={styles.secondary}>{CREDIT_HOLD_EXPLANATION}</Text>}
    </View> : <View style={styles.ledger}>
      {row('Balance', credits?.totalAvailable)}
      {!credits?.business && row('On hold', credits?.reservedCredits)}
      {row('Available', available, true)}
      {requiredCredits > 0 && <View style={[styles.row, styles.estimate]}>
        <Text size="s" style={[styles.secondary, styles.label]}>Estimated cost</Text>
        <Text size="s" weight="semibold" style={styles.primary}>{formatCreditBalance(Math.ceil(requiredCredits))} credits</Text>
      </View>}
    </View>}
  </>;
}
